import React, { useState, useEffect } from 'react';
import { managerAPI } from '../../../config/api';
import APP_COLOURS from '../../../config/colors';
import moment from 'moment';

const StaffProductivitySheets = ({ selectedStaff }) => {
  const [commissions, setCommissions] = useState([]);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [productivityData, setProductivityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewCommissionForm, setShowNewCommissionForm] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);

  // New commission form state
  const [newCommission, setNewCommission] = useState({
    title: '',
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().add(30, 'days').format('YYYY-MM-DD'),
    target: 0,
  });

  useEffect(() => {
    if (selectedStaff) {
      fetchCommissions();
    }
  }, [selectedStaff]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const data = await managerAPI.getCommissions(selectedStaff.id);
      setCommissions(data.commissions || []);
      setError('');
    } catch (err) {
      console.error('Error fetching commissions:', err);
      setError(err.message || 'Failed to fetch commissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductivitySheet = async (commissionId) => {
    try {
      setLoading(true);
      const data = await managerAPI.getProductivitySheet(commissionId);
      setProductivityData(data);
      setError('');
    } catch (err) {
      console.error('Error fetching productivity sheet:', err);
      setError(err.message || 'Failed to fetch productivity sheet');
    } finally {
      setLoading(false);
    }
  };

  const handleCommissionSelect = (commission) => {
    setSelectedCommission(commission);
    setShowNewCommissionForm(false);
    fetchProductivitySheet(commission.id);
  };

  const handleCreateCommission = async () => {
    if (!newCommission.title || !newCommission.target) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await managerAPI.createCommission(selectedStaff.id, newCommission);
      setShowNewCommissionForm(false);
      setNewCommission({
        title: '',
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(30, 'days').format('YYYY-MM-DD'),
        target: 0,
      });
      fetchCommissions();
      setError('');
    } catch (err) {
      console.error('Error creating commission:', err);
      setError(err.message || 'Failed to create commission');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCommission = async (commissionId) => {
    if (!window.confirm('Are you sure you want to delete this productivity sheet?')) {
      return;
    }

    try {
      setLoading(true);
      await managerAPI.deleteCommission(commissionId);
      setSelectedCommission(null);
      setProductivityData(null);
      fetchCommissions();
      setError('');
    } catch (err) {
      console.error('Error deleting commission:', err);
      setError(err.message || 'Failed to delete commission');
    } finally {
      setLoading(false);
    }
  };

  const handleViewFullSheet = async () => {
    if (!selectedCommission) return;

    try {
      setGeneratingToken(true);
      const response = await managerAPI.generateProductivitySheetToken(selectedCommission.id);
      
      // Construct the frontend public URL using the token
      const publicUrl = `${window.location.origin}/productivity-sheet/${response.token}`;
      
      // Open in new tab - the shareable link will be on that page
      window.open(publicUrl, '_blank');
      
      setError('');
    } catch (err) {
      console.error('Error generating sheet URL:', err);
      setError(err.message || 'Failed to generate shareable link');
    } finally {
      setGeneratingToken(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const currencyFormat = (num) => {
    return 'R ' + parseFloat(num).toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_signature': return '#FFA500';
      case 'pending_manager': return '#FFD700';
      case 'active': return '#4CAF50';
      case 'completed': return '#2196F3';
      case 'cancelled': return '#F44336';
      default: return APP_COLOURS.GRAY;
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      pending_signature: 'Awaiting Signatures',
      pending_manager: 'Pending Manager',
      active: 'Active',
      completed: 'Completed',
      cancelled: 'Cancelled',
    };
    return statusMap[status] || status;
  };

  if (loading && commissions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
          <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading productivity sheets...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ }}>
      {/* Error Message */}
      {error && (
        <div className="mb-6 rounded-xl p-4" style={{ backgroundColor: 'rgba(255, 95, 95, 0.1)', border: `1px solid ${APP_COLOURS.ERROR}` }}>
          <div className="flex items-center">
            <i className="fas fa-exclamation-circle mr-3" style={{ color: APP_COLOURS.ERROR }}></i>
            <p style={{ color: APP_COLOURS.ERROR }}>{error}</p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px', height: '600px' }}>
        {/* Left Sidebar - Targets List */}
        <div style={{ width: '300px', flexShrink: 0 }}>
          <div className="rounded-xl p-4" style={{  height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Targets
                </h3>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  {commissions.length} {commissions.length === 1 ? 'target' : 'targets'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowNewCommissionForm(!showNewCommissionForm);
                  setSelectedCommission(null);
                  setProductivityData(null);
                }}
                className="px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: APP_COLOURS.BLUE,
                  color: 'white',
                }}
              >
                <i className="fas fa-plus mr-1"></i>
                New
              </button>
            </div>

            {/* New Commission Form */}
            {showNewCommissionForm && (
              <div className="mb-4 rounded-lg p-3" style={{ backgroundColor: APP_COLOURS.BG3 }}>
                <h4 className="text-sm font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  New Target
                </h4>
                <input
                  type="text"
                  placeholder="Title *"
                  value={newCommission.title}
                  onChange={(e) => setNewCommission({ ...newCommission, title: e.target.value })}
                  className="w-full mb-2 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: APP_COLOURS.BG4,
                    color: APP_COLOURS.TEXTCOLOR,
                    border: `1px solid ${APP_COLOURS.BG5}`,
                  }}
                />
                <label className="text-xs block mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={newCommission.startDate}
                  onChange={(e) => setNewCommission({ ...newCommission, startDate: e.target.value })}
                  className="w-full mb-2 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: APP_COLOURS.BG4,
                    color: APP_COLOURS.TEXTCOLOR,
                    border: `1px solid ${APP_COLOURS.BG5}`,
                  }}
                />
                <label className="text-xs block mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={newCommission.endDate}
                  onChange={(e) => setNewCommission({ ...newCommission, endDate: e.target.value })}
                  className="w-full mb-2 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: APP_COLOURS.BG4,
                    color: APP_COLOURS.TEXTCOLOR,
                    border: `1px solid ${APP_COLOURS.BG5}`,
                  }}
                />
                <label className="text-xs block mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  Target Amount (R) *
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={newCommission.target}
                  onChange={(e) => setNewCommission({ ...newCommission, target: parseFloat(e.target.value) || 0 })}
                  className="w-full mb-3 px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: APP_COLOURS.BG4,
                    color: APP_COLOURS.TEXTCOLOR,
                    border: `1px solid ${APP_COLOURS.BG5}`,
                  }}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateCommission}
                    disabled={loading}
                    className="flex-1 px-3 py-2 rounded-lg text-sm font-medium"
                    style={{
                      backgroundColor: APP_COLOURS.BLUE,
                      color: 'white',
                      opacity: loading ? 0.6 : 1,
                    }}
                  >
                    {loading ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    onClick={() => setShowNewCommissionForm(false)}
                    className="flex-1 px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      color: APP_COLOURS.TEXTCOLOR,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Commission List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {commissions.length === 0 ? (
                <div className="text-center py-8">
                  <i className="fas fa-bullseye text-4xl mb-3" style={{ color: APP_COLOURS.PLACEHOLDER, opacity: 0.3 }}></i>
                  <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                    No targets yet.
                    <br />
                    Click "New" to create one.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {commissions.map((commission) => {
                    // Calculate signatures status
                    const staffSigned = !!commission.staffSignature;
                    const managerSigned = !!commission.managerSignature;
                    const bothSigned = staffSigned && managerSigned;
                    const neitherSigned = !staffSigned && !managerSigned;
                    
                    return (
                      <button
                        key={commission.id}
                        onClick={() => handleCommissionSelect(commission)}
                        className="w-full text-left rounded-lg p-3 transition-all duration-200"
                        style={{
                          backgroundColor: selectedCommission?.id === commission.id ? APP_COLOURS.BG4 : APP_COLOURS.BG3,
                          borderLeft: selectedCommission?.id === commission.id ? `3px solid ${APP_COLOURS.BLUE}` : 'none',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (selectedCommission?.id !== commission.id) {
                            e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedCommission?.id !== commission.id) {
                            e.currentTarget.style.backgroundColor = APP_COLOURS.BG3;
                          }
                        }}
                      >
                        <p className="font-medium mb-1 text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                          {commission.title}
                        </p>
                        <p className="text-xs mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                          {moment(commission.startDate).format('DD MMM')} - {moment(commission.endDate).format('DD MMM YYYY')}
                        </p>
                        
                        {/* Target Amount */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Target:
                          </span>
                          <span className="text-xs font-semibold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {currencyFormat(commission.target)}
                          </span>
                        </div>

                        {/* Achievement Percentage */}
                        <div className="mb-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                              Achievement:
                            </span>
                            <span 
                              className="text-xs font-bold"
                              style={{ 
                                color: (commission.percToTarget || 0) >= 100 
                                  ? APP_COLOURS.GREEN 
                                  : APP_COLOURS.ORANGE
                              }}
                            >
                              {(commission.percToTarget || 0).toFixed(1)}%
                            </span>
                          </div>
                          {/* Progress Bar */}
                          <div style={{
                            width: '100%',
                            height: 4,
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}>
                            <div style={{
                              width: `${Math.min(commission.percToTarget || 0, 100)}%`,
                              height: '100%',
                              backgroundColor: (commission.percToTarget || 0) >= 100 
                                ? APP_COLOURS.GREEN 
                                : APP_COLOURS.ORANGE,
                              transition: 'width 0.3s ease',
                            }} />
                          </div>
                        </div>

                        {/* Signature Status Icons */}
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex items-center" title={staffSigned ? 'Staff Signed' : 'Staff Pending'}>
                            <i 
                              className={`fas ${staffSigned ? 'fa-user-check' : 'fa-user-clock'} text-xs mr-1`}
                              style={{ 
                                color: staffSigned ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER,
                              }}
                            ></i>
                            <span className="text-xs" style={{ 
                              color: staffSigned ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER,
                            }}>
                              Staff
                            </span>
                          </div>
                          <div className="flex items-center" title={managerSigned ? 'Manager Signed' : 'Manager Pending'}>
                            <i 
                              className={`fas ${managerSigned ? 'fa-user-check' : 'fa-user-clock'} text-xs mr-1`}
                              style={{ 
                                color: managerSigned ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER,
                              }}
                            ></i>
                            <span className="text-xs" style={{ 
                              color: managerSigned ? APP_COLOURS.GREEN : APP_COLOURS.PLACEHOLDER,
                            }}>
                              Manager
                            </span>
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div>
                          <span
                            className="text-xs px-2 py-1 rounded"
                            style={{
                              backgroundColor: getStatusColor(commission.status) + '33',
                              color: getStatusColor(commission.status),
                            }}
                          >
                            {getStatusLabel(commission.status)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right - Productivity Sheet Display */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {productivityData ? (
            <div className="rounded-xl p-6" style={{ backgroundColor: APP_COLOURS.BG2 }} id="productivity-sheet-content">
              {/* Action Buttons */}
              <div className="mb-6 no-print">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Productivity Sheet
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={handleViewFullSheet}
                      disabled={generatingToken}
                      className="px-4 py-2 rounded-lg text-sm transition-all duration-200 font-medium"
                      style={{
                        backgroundColor: APP_COLOURS.PURPLE || '#9333ea',
                        color: 'white',
                        opacity: generatingToken ? 0.6 : 1,
                      }}
                    >
                      <i className={`fas ${generatingToken ? 'fa-spinner fa-spin' : 'fa-external-link-alt'} mr-2`}></i>
                      {generatingToken ? 'Generating...' : 'View & Share Full Sheet'}
                    </button>
                    <button
                      onClick={handlePrint}
                      className="px-3 py-2 rounded-lg text-sm transition-all duration-200"
                      style={{
                        backgroundColor: APP_COLOURS.BLUE,
                        color: 'white',
                      }}
                    >
                      <i className="fas fa-print mr-2"></i>
                      Print
                    </button>
                    <button
                      onClick={() => handleDeleteCommission(selectedCommission.id)}
                      className="px-3 py-2 rounded-lg text-sm transition-all duration-200"
                      style={{
                        backgroundColor: APP_COLOURS.ERROR,
                        color: 'white',
                      }}
                    >
                      <i className="fas fa-trash mr-2"></i>
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {/* Sheet Header */}
              <div className="mb-6 pb-4" style={{ borderBottom: `1px solid ${APP_COLOURS.BG4}` }}>
                <h3 className="text-2xl font-bold mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  {productivityData.period.title}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>Period</p>
                    <p className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {moment(productivityData.period.startDate).format('DD MMM YYYY')} -{' '}
                      {moment(productivityData.period.endDate).format('DD MMM YYYY')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>Staff Member</p>
                    <p className="font-medium" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {productivityData.staff.first_name} {productivityData.staff.last_name}
                    </p>
                    <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                      {productivityData.staff.position}
                    </p>
                  </div>
                </div>
              </div>

              {/* Sales Metrics */}
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  <i className="fas fa-chart-line mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                  Sales Performance
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <MetricCard
                    title="Target"
                    value={currencyFormat(productivityData.salesMetrics.targetAmount)}
                    icon="bullseye"
                  />
                  <MetricCard
                    title="Actual Sales"
                    value={currencyFormat(productivityData.salesMetrics.actualSales)}
                    icon="dollar-sign"
                    highlight={true}
                  />
                  <MetricCard
                    title="Achievement"
                    value={`${productivityData.salesMetrics.percToTarget.toFixed(1)}%`}
                    icon="chart-line"
                    color={productivityData.salesMetrics.percToTarget >= 100 ? APP_COLOURS.GREEN : APP_COLOURS.ORANGE}
                  />
                  <MetricCard
                    title="Transactions"
                    value={productivityData.salesMetrics.totalTransactions}
                    icon="shopping-cart"
                  />
                  <MetricCard
                    title="Avg Sale Value"
                    value={currencyFormat(productivityData.salesMetrics.averageArs)}
                    icon="calculator"
                  />
                  <MetricCard
                    title="Total Units"
                    value={productivityData.salesMetrics.totalUnits.toFixed(0)}
                    icon="boxes"
                  />
                </div>
              </div>

              {/* Attendance Metrics */}
              <div className="mb-6">
                <h4 className="text-lg font-bold mb-4 flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  <i className="fas fa-calendar-check mr-2" style={{ color: APP_COLOURS.BLUE }}></i>
                  Attendance
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <MetricCard
                    title="Days Present"
                    value={productivityData.attendanceMetrics.totalDaysPresent}
                    icon="calendar-check"
                  />
                  <MetricCard
                    title="Total Hours"
                    value={productivityData.attendanceMetrics.totalHours.toFixed(1)}
                    icon="clock"
                  />
                  <MetricCard
                    title="Avg Hours/Day"
                    value={productivityData.attendanceMetrics.averageHoursPerDay.toFixed(1)}
                    icon="hourglass-half"
                  />
                </div>
              </div>

              {/* Summary Stats */}
              <div className="mb-6 rounded-lg p-4" style={{ backgroundColor: APP_COLOURS.BG3 }}>
                <h4 className="text-md font-semibold mb-3" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                  Summary
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                      Average Items per Transaction
                    </p>
                    <p className="text-lg font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {productivityData.salesMetrics.averageItp.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                      Total Sales Records
                    </p>
                    <p className="text-lg font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      {productivityData.sales.length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Signatures Section */}
              {productivityData.commission.status !== 'cancelled' && (
                <div className="pt-6" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
                  <h4 className="text-lg font-bold mb-4" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    Sign-off Status
                  </h4>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Staff Signature */}
                    <div 
                      className="rounded-lg p-4" 
                      style={{ 
                        backgroundColor: APP_COLOURS.BG3,
                        border: productivityData.commission.staffSignature 
                          ? `2px solid ${APP_COLOURS.GREEN}`
                          : `2px solid ${APP_COLOURS.ORANGE}`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                          Staff Signature
                        </p>
                        {productivityData.commission.staffSignature ? (
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{
                              backgroundColor: APP_COLOURS.GREEN + '33',
                              color: APP_COLOURS.GREEN,
                            }}
                          >
                            <i className="fas fa-check-circle mr-1"></i>
                            Signed
                          </span>
                        ) : (
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{
                              backgroundColor: APP_COLOURS.ORANGE + '33',
                              color: APP_COLOURS.ORANGE,
                            }}
                          >
                            <i className="fas fa-clock mr-1"></i>
                            Pending
                          </span>
                        )}
                      </div>
                      
                      {productivityData.commission.staffSignature ? (
                        <div>
                          {productivityData.commission.staffSignatureImage && (
                            <img 
                              src={productivityData.commission.staffSignatureImage} 
                              alt="Staff Signature"
                              style={{ 
                                maxHeight: 40, 
                                marginBottom: 8,
                                backgroundColor: '#fff',
                                padding: 4,
                                borderRadius: 4,
                                border: '1px solid #ddd',
                              }}
                            />
                          )}
                          <p className="font-semibold mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {productivityData.commission.staffSignature}
                          </p>
                          <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Signed: {moment(productivityData.commission.staffSignatureDate).format('DD MMM YYYY HH:mm')}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm italic mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Awaiting staff signature
                          </p>
                          <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Share the full sheet link with the staff member to sign
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Manager Signature */}
                    <div 
                      className="rounded-lg p-4" 
                      style={{ 
                        backgroundColor: APP_COLOURS.BG3,
                        border: productivityData.commission.managerSignature 
                          ? `2px solid ${APP_COLOURS.GREEN}`
                          : `2px solid ${APP_COLOURS.ORANGE}`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                          Manager Signature
                        </p>
                        {productivityData.commission.managerSignature ? (
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{
                              backgroundColor: APP_COLOURS.GREEN + '33',
                              color: APP_COLOURS.GREEN,
                            }}
                          >
                            <i className="fas fa-check-circle mr-1"></i>
                            Signed
                          </span>
                        ) : (
                          <span 
                            className="text-xs px-2 py-1 rounded"
                            style={{
                              backgroundColor: APP_COLOURS.ORANGE + '33',
                              color: APP_COLOURS.ORANGE,
                            }}
                          >
                            <i className="fas fa-clock mr-1"></i>
                            Pending
                          </span>
                        )}
                      </div>
                      
                      {productivityData.commission.managerSignature ? (
                        <div>
                          {productivityData.commission.managerSignatureImage && (
                            <img 
                              src={productivityData.commission.managerSignatureImage} 
                              alt="Manager Signature"
                              style={{ 
                                maxHeight: 40, 
                                marginBottom: 8,
                                backgroundColor: '#fff',
                                padding: 4,
                                borderRadius: 4,
                                border: '1px solid #ddd',
                              }}
                            />
                          )}
                          <p className="font-semibold mb-1" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {productivityData.commission.managerSignature}
                          </p>
                          <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Signed: {moment(productivityData.commission.managerSignatureDate).format('DD MMM YYYY HH:mm')}
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm italic mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Awaiting manager signature
                          </p>
                          <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            Open the full sheet to sign as manager
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Overall Sign-off Status */}
                  <div className="mt-4 rounded-lg p-3 text-center" style={{ 
                    backgroundColor: productivityData.commission.staffSignature && productivityData.commission.managerSignature
                      ? APP_COLOURS.GREEN + '22'
                      : APP_COLOURS.ORANGE + '22',
                  }}>
                    {productivityData.commission.staffSignature && productivityData.commission.managerSignature ? (
                      <p style={{ 
                        color: APP_COLOURS.GREEN,
                        fontSize: 14,
                        fontWeight: '600',
                      }}>
                        <i className="fas fa-check-circle mr-2"></i>
                        Both parties have signed - Sheet is complete
                      </p>
                    ) : (
                      <p style={{ 
                        color: APP_COLOURS.ORANGE,
                        fontSize: 14,
                        fontWeight: '600',
                      }}>
                        <i className="fas fa-hourglass-half mr-2"></i>
                        Waiting for {!productivityData.commission.staffSignature && !productivityData.commission.managerSignature 
                          ? 'both signatures'
                          : !productivityData.commission.staffSignature 
                            ? 'staff signature' 
                            : 'manager signature'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-6 pt-4 text-center" style={{ borderTop: `1px solid ${APP_COLOURS.BG4}` }}>
                <p className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  Generated on {moment(productivityData.generatedAt).format('DD MMM YYYY HH:mm')}
                </p>
              </div>
            </div>
          ) : loading ? (
            <div className="rounded-xl p-12 text-center" style={{ backgroundColor: APP_COLOURS.BG2 }}>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: APP_COLOURS.BLUE }}></div>
              <p style={{ color: APP_COLOURS.TEXTCOLOR }}>Loading productivity sheet...</p>
            </div>
          ) : (
            <div className="rounded-xl p-12 text-center" style={{ backgroundColor: APP_COLOURS.BG2 }}>
              <i className="fas fa-chart-bar text-6xl mb-4" style={{ color: APP_COLOURS.PLACEHOLDER, opacity: 0.3 }}></i>
              <p className="text-xl mb-2" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Select a Target
              </p>
              <p className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                Choose a target from the list to view its productivity sheet
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          #productivity-sheet-content {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon, highlight, color }) => {
  return (
    <div 
      className="rounded-lg p-4"
      style={{
        backgroundColor: highlight ? APP_COLOURS.BLUE + '22' : APP_COLOURS.BG3,
        borderLeft: highlight ? `3px solid ${APP_COLOURS.BLUE}` : 'none',
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium" style={{ color: APP_COLOURS.PLACEHOLDER }}>
          {title}
        </p>
        <i className={`fas fa-${icon}`} style={{ color: color || APP_COLOURS.BLUE, opacity: 0.7 }}></i>
      </div>
      <p className="text-xl font-bold" style={{ color: color || APP_COLOURS.TEXTCOLOR }}>
        {value}
      </p>
    </div>
  );
};

export default StaffProductivitySheets;

